import React, { useEffect, useState } from "react";
import { Alert, Box, Radio, Skeleton, Typography, useTheme } from "@mui/material";
import { IconType } from "react-icons/lib";
import { IoPrint } from "react-icons/io5";
import { MdAutoFixHigh } from "react-icons/md";
import { RequestFormContext } from "../RequestFormContext";
import { RequestMagicLinkMethod } from "../Enums/RequestMagicLinkMethod";
import { SignatureMethod } from "../Enums/SignatureMethod";
import { MagicLinkIdCardUploadMethod } from "../Enums/MagicLinkIdCardUploadMethod";
import { CupertinoSegmentPicker } from "./CupertinoSegmentPicker";
import { MagicLinkContactPicker } from "./MagicLinkContactPicker";

export type SignatureMethodCardProps = {
    title: string;
    description: string;
    icon: IconType;
    suffix?: React.ReactNode;
    selectedValue: SignatureMethod | null;
    value: SignatureMethod;
    onSelect: (value: SignatureMethod) => void;
    disabled?: boolean;
    errorMessage?: string | null;
};

export const SignatureMethodCard = (props: SignatureMethodCardProps) => {
    const theme = useTheme();
    const IconComponent = props.icon;
    const isSelected = props.selectedValue === props.value;
    const isDisabled = props.disabled === true;

    const handleSelect = () => {
        if (isDisabled) {
            return;
        }
        props.onSelect(props.value);
    };

    return (
        <Box
            onClick={handleSelect}
            aria-disabled={isDisabled}
            sx={{
                borderRadius: "7px",
                border: isSelected && !isDisabled
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${isDisabled ? theme.palette.error.light : theme.palette.divider}`,
                paddingX: "24px",
                paddingY: "24px",
                cursor: isDisabled ? "not-allowed" : "pointer",
                transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                backgroundColor: isDisabled ? theme.palette.grey[50] : theme.palette.common.white,
                opacity: isDisabled ? 0.85 : 1,
                boxShadow: isSelected && !isDisabled
                    ? `0 0 0 1px ${theme.palette.primary.main}22, 0 4px 14px rgba(0,0,0,0.06)`
                    : "0 1px 3px rgba(0,0,0,0.04)",
                display: "flex",
                gap: theme.spacing(2),
                "&:hover": isDisabled
                    ? undefined
                    : {
                        borderColor: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
                        boxShadow: isSelected
                            ? `0 0 0 1px ${theme.palette.primary.main}33, 0 6px 18px rgba(0,0,0,0.08)`
                            : "0 2px 8px rgba(0,0,0,0.06)",
                    },
            }}
        >
            <Radio
                checked={isSelected && !isDisabled}
                value={props.value}
                disabled={isDisabled}
                onChange={handleSelect}
                sx={{
                    alignSelf: "flex-start",
                    padding: 0,
                    marginTop: "4px",
                    "& .MuiSvgIcon-root": {
                        fontSize: 20,
                    },
                    color: isSelected ? undefined : theme.palette.text.secondary,
                    "&.Mui-checked": {
                        color: theme.palette.primary.main,
                    },
                }}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flexGrow: 1, width: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                        variant="h6"
                        component="p"
                        sx={{ color: isDisabled ? "text.disabled" : undefined }}
                    >
                        {props.title}
                    </Typography>
                    <IconComponent
                        size={18}
                        color={isDisabled ? theme.palette.text.disabled : theme.palette.primary.main}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {props.description}
                </Typography>
                {props.errorMessage && (
                    <Alert
                        severity="error"
                        sx={{
                            mt: 1,
                            padding: "12px 16px",
                            ".MuiAlert-message": { padding: 0 },
                        }}
                    >
                        {props.errorMessage}
                    </Alert>
                )}
                {props.suffix && !isDisabled && (
                    <Box sx={{ marginTop: 2 }}>
                        {props.suffix}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export type ManualSignMethodCardProps = {
    selectedValue: SignatureMethod | null;
    value: SignatureMethod;
    onSelect: (value: SignatureMethod) => void;
    suffix?: React.ReactNode;
};

export const ManualSignMethodCard = (props: ManualSignMethodCardProps) => {
    return (
        <SignatureMethodCard
            title="Manuálny podpis - Vytlačiť a podpísať"
            description="Vytlačte žiadosť a odovzdajte ju klientovi na podpis. Po podpise klientom skontrolujte, či je všetko vyplnené správne, a následne nahrajte podpísanú žiadosť do systému."
            icon={IoPrint}
            selectedValue={props.selectedValue}
            value={props.value}
            onSelect={props.onSelect}
            suffix={props.suffix}
        />
    );
};

export type MagicLinkSignMethodCardProps = {
    selectedValue: SignatureMethod | null;
    value: SignatureMethod;
    onSelect: (value: SignatureMethod) => void;
    magicLinkUploadMethod: MagicLinkIdCardUploadMethod | null;
    setMagicLinkUploadMethod: (method: MagicLinkIdCardUploadMethod | null) => void;
    magicLinkDeliveryMethod: RequestMagicLinkMethod | null;
    setMagicLinkDeliveryMethod: (method: RequestMagicLinkMethod | null) => void;
    magicLinkEmails: string[];
    magicLinkPhones: string[];
    magicLinkRecipient: string | null;
    setMagicLinkRecipient: (recipient: string | null) => void;
    isIdCardUploadRequiredForRequest: boolean;
    loadMagicLinkConfigTask: RequestFormContext["loadMagicLinkConfigTask"];
    onUnavailable?: () => void;
};

const NO_VERIFIED_CONTACTS_MESSAGE =
    "Pre klienta nie sú k dispozícii žiadne overené emailové adresy ani telefónne čísla. Prosím použite iný spôsob podpisu.";

export const MagicLinkSignMethodCard = ({
    magicLinkEmails,
    magicLinkPhones,
    magicLinkRecipient,
    setMagicLinkRecipient,
    magicLinkDeliveryMethod: deliveryMethod = RequestMagicLinkMethod.Sms,
    setMagicLinkDeliveryMethod,
    magicLinkUploadMethod = MagicLinkIdCardUploadMethod.You,
    setMagicLinkUploadMethod,
    isIdCardUploadRequiredForRequest,
    loadMagicLinkConfigTask,
    onUnavailable,
    ...props
}: MagicLinkSignMethodCardProps) => {
    const isRunning = loadMagicLinkConfigTask.isRunning;
    const [hasNoVerifiedContacts, setHasNoVerifiedContacts] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const loadConfig = async () => {
            // Contacts are derived from the partner/contract selected in the form, and the user can
            // still change those after visiting this view, so previous values must never be reused.
            const result = await loadMagicLinkConfigTask.perform();
            if (cancelled || !result) {
                return;
            }

            const emails = result.emails || [];
            const phones = result.phones || [];
            if (emails.length === 0 && phones.length === 0) {
                setHasNoVerifiedContacts(true);
            }
        };

        void loadConfig();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (hasNoVerifiedContacts && props.selectedValue === props.value) {
            onUnavailable?.();
        }
    }, [hasNoVerifiedContacts, onUnavailable, props.selectedValue, props.value]);

    const isMagicLinkSelected = props.selectedValue === props.value;
    const theme = useTheme();

    const magicLinkSuffix = isMagicLinkSelected && !hasNoVerifiedContacts ? (
        <>
            {isRunning ? (
                <>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, marginTop: 2 }}>
                        <Skeleton variant="text" width={200} height={24} />
                        <Box
                            sx={{
                                display: "flex",
                                borderRadius: "7px",
                                backgroundColor: theme.palette.grey[100],
                                border: `1px solid ${theme.palette.divider}`,
                                padding: "4px",
                                gap: "4px",
                            }}
                        >
                            <Skeleton variant="rectangular" sx={{ flex: 1, borderRadius: "5px", height: 80 }} />
                            <Skeleton variant="rectangular" sx={{ flex: 1, borderRadius: "5px", height: 80 }} />
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, marginTop: 2 }}>
                        <Skeleton variant="text" width={200} height={24} />
                        <Box
                            sx={{
                                display: "flex",
                                borderRadius: "7px",
                                backgroundColor: theme.palette.grey[100],
                                border: `1px solid ${theme.palette.divider}`,
                                padding: "4px",
                                gap: "4px",
                            }}
                        >
                            <Skeleton variant="rectangular" sx={{ flex: 1, borderRadius: "5px", height: 60 }} />
                            <Skeleton variant="rectangular" sx={{ flex: 1, borderRadius: "5px", height: 60 }} />
                        </Box>
                    </Box>
                </>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <MagicLinkContactPicker
                        magicLinkEmails={magicLinkEmails}
                        magicLinkPhones={magicLinkPhones}
                        magicLinkRecipient={magicLinkRecipient}
                        setMagicLinkRecipient={setMagicLinkRecipient}
                        magicLinkDeliveryMethod={deliveryMethod}
                        setMagicLinkDeliveryMethod={setMagicLinkDeliveryMethod}
                    />

                    {!isIdCardUploadRequiredForRequest && (
                        <CupertinoSegmentPicker<MagicLinkIdCardUploadMethod>
                            label="Výber spôsobu nahratia občianskeho preukazu klienta"
                            options={[
                                {
                                    value: MagicLinkIdCardUploadMethod.You,
                                    label: "Obchodník nahrá OP"
                                },
                                {
                                    value: MagicLinkIdCardUploadMethod.Client,
                                    label: "Klient si nahrá OP"
                                }
                            ]}
                            selectedValue={magicLinkUploadMethod}
                            onSelect={setMagicLinkUploadMethod}
                        />
                    )}
                </Box>
            )}
        </>
    ) : undefined;

    return (
        <SignatureMethodCard
            title="Magic Link - Dokončite podpis žiadosti online"
            description="Vyberte, akým spôsobom chcete klientovi odoslať Magic Link. Klientov občiansky preukaz môžete nahrať vy pri osobnom stretnutí, alebo si klient nahrá OP sám cez link."
            icon={MdAutoFixHigh}
            selectedValue={props.selectedValue}
            value={props.value}
            onSelect={props.onSelect}
            suffix={magicLinkSuffix}
            disabled={hasNoVerifiedContacts}
            errorMessage={hasNoVerifiedContacts ? NO_VERIFIED_CONTACTS_MESSAGE : null}
        />
    );
};

